/**
 * Map URL path patterns to translation namespaces for lazy loading.
 * First matching rule wins. Namespace slugs must match translation_namespaces.slug in DB.
 */
const pathNamespaces = [
  { path: /^\/exchange-control-panel\/admin-login(\/|$)/, namespaces: ['common', 'auth', 'validation'] },
  { path: /^\/exchange-control-panel\/reports(\/|$)/, namespaces: ['common', 'admin', 'admin_reports', 'report'] },
  { path: /^\/exchange-control-panel\/prop(\/|$)/, namespaces: ['common', 'admin', 'prop'] },
  { path: /^\/exchange-control-panel\/(peer-trades[^/]*|peer-merchant-documents)(\/|$)/, namespaces: ['common', 'admin', 'admin_p2p', 'p2p'] },
  { path: /^\/exchange-control-panel\/translation-v2(\/|$)/, namespaces: ['common', 'admin'] },
  { path: /^\/exchange-control-panel\/loans(\/|$)/, namespaces: ['common', 'admin', 'admin_loans', 'loan'] },
  { path: /^\/exchange-control-panel(\/|$)/, namespaces: ['common', 'admin'] },
  { path: /^\/user\/profile(\/|$)/, namespaces: ['common', 'profile'] },
  { path: /^\/user(\/|$)/, namespaces: ['common', 'profile'] },
  { path: /^\/trade(\/|$)/, namespaces: ['common', 'market'] },
  { path: /^\/market(\/|$)/, namespaces: ['common', 'market'] },
  { path: /^\/options-market(\/|$)/, namespaces: ['common', 'market', 'options'] },
  { path: /^\/futures-market(\/|$)/, namespaces: ['common', 'market', 'futures_market'] },
  { path: /^\/markets\/data(\/|$)/, namespaces: ['common', 'trading_data'] },
  { path: /^\/markets(\/|$)/, namespaces: ['common', 'markets_list'] },
  { path: /^\/price(\/|$)/, namespaces: ['common', 'coin_info'] },
  { path: /^\/heatmap(\/|$)/, namespaces: ['common', 'heat_map'] },
  { path: /^\/earn(\/|$)/, namespaces: ['common', 'staking'] },
  { path: /^\/wallet(\/|$)/, namespaces: ['common', 'wallet'] },
  { path: /^\/wallets(\/|$)/, namespaces: ['common', 'wallet'] },
  { path: /^\/deposit(\/|$)/, namespaces: ['common', 'wallet'] },
  { path: /^\/withdraw(\/|$)/, namespaces: ['common', 'wallet'] },
  { path: /^\/reports(\/|$)/, namespaces: ['common', 'report', 'reports'] },
  { path: /^\/orders\/order-history(\/|$)/, namespaces: ['common', 'report', 'reports'] },
  { path: /^\/orders(\/|$)/, namespaces: ['common', 'report', 'reports'] },
  { path: /^\/convert(\/|$)/, namespaces: ['common', 'swap'] },
  { path: /^\/swap(\/|$)/, namespaces: ['common', 'swap'] },
  { path: /^\/auth(\/|$)/, namespaces: ['common', 'auth'] },
  { path: /^\/license(\/|$)/, namespaces: ['common', 'auth'] },
  { path: /^\/login/, namespaces: ['common', 'auth'] },
  { path: /^\/register/, namespaces: ['common', 'auth'] },
  { path: /^\/forgot-password/, namespaces: ['common', 'auth', 'passwords', 'validation'] },
  { path: /^\/reset-password/, namespaces: ['common', 'auth', 'passwords', 'validation'] },
  { path: /^\/email\/verify/, namespaces: ['common', 'auth'] },
  { path: /^\/phone\/verify/, namespaces: ['common', 'auth'] },
  { path: /^\/password\/confirm/, namespaces: ['common', 'auth', 'validation'] },
  { path: /^\/login-two-factor-challenge/, namespaces: ['common', 'auth'] },
  // Fortify serves the two-factor challenge page itself at /two-factor-challenge,
  // which the rule above does not match. Social sign-in redirects here.
  { path: /^\/two-factor-challenge/, namespaces: ['common', 'auth'] },
  // Where social sign-ins land when the provider gives us no email address.
  { path: /^\/set-email/, namespaces: ['common', 'auth'] },
  { path: /^\/staking(\/|$)/, namespaces: ['common', 'staking'] },
  { path: /^\/p2p-trading(\/|$)/, namespaces: ['common', 'p2p'] },
  { path: /^\/p2p(\/|$)/, namespaces: ['common', 'p2p'] },
  { path: /^\/loans(\/|$)/, namespaces: ['common', 'loan'] },
  { path: /^\/tasks(\/|$)/, namespaces: ['common', 'tasks'] },
  { path: /^\/$/, namespaces: ['common', 'dashboard'] },
  { path: /^\/academy(\/|$)/, namespaces: ['common', 'article'] },
  { path: /^\/blog(\/|$)/, namespaces: ['common', 'article'] },
  { path: /^\/article(s)?(\/|$)/, namespaces: ['common', 'article'] },
  { path: /^\/ambassador(\/|$)/, namespaces: ['common', 'ambassador', 'page', 'profile'] },
  { path: /^\/(fees|trade-rule|page)(\/|$)/, namespaces: ['common', 'page'] },
  { path: /^\/launchpad(s)?(\/|$)/, namespaces: ['common', 'launchpad'] },
  { path: /^\/download(\/|$)/, namespaces: ['common', 'download'] },
  { path: /^\/vip(\/|$)/, namespaces: ['common', 'vip'] },
  { path: /^\/errors\//, namespaces: ['common', 'error'] },
  { path: /^\/prop(\/|$)/, namespaces: ['common', 'prop'] },
  { path: /^\//, namespaces: ['common'] },
];

/**
 * Get translation namespaces for a given URL path.
 * @param {string} path - e.g. "/trade/BTC-USDT"
 * @returns {string[]} - e.g. ['common', 'market']
 */
export function getNamespacesForPath(path) {
  const normalized = path.replace(/\/$/, '') || '/';
  for (const { path: pattern, namespaces } of pathNamespaces) {
    if (typeof pattern === 'string') {
      if (normalized === pattern || normalized.startsWith(pattern + '/')) {
        return [...namespaces];
      }
    } else if (pattern.test(normalized)) {
      return [...namespaces];
    }
  }
  return ['common'];
}

export { pathNamespaces };
